import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage, Company, Mode } from '../lib/types';
import { getCompanies, postChatStream, postUpload } from '../lib/api';

function uid(): string {
  return (crypto as any)?.randomUUID?.() ?? `id-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
}

export function useChat() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState('de-graaf');
  const [division, setDivision] = useState('Alle');
  const [mode, setMode] = useState<Mode>('qa');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const conversationId = useRef(uid());

  useEffect(() => {
    getCompanies()
      .then((cs) => {
        setCompanies(cs);
        if (cs.length && !cs.find((c) => c.slug === company)) setCompany(cs[0].slug);
      })
      .catch(() => setCompanies([{ slug: 'de-graaf', name: 'De Graaf', divisions: ['Alle'] }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newChat = useCallback(() => {
    conversationId.current = uid();
    setMessages([]);
    setInput('');
  }, []);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || loading) return;

      const userMsg: ChatMessage = { id: uid(), role: 'user', content };
      setMessages((m) => [...m, userMsg]);
      setInput('');
      setLoading(true);
      // The assistant bubble is created empty and filled as deltas arrive, so
      // the user sees words about a second in rather than a blank six seconds.
      const replyId = uid();
      setMessages((m) => [...m, { id: replyId, role: 'assistant', content: '' }]);

      const patch = (fields: Partial<ChatMessage>) =>
        setMessages((m) => m.map((msg) => (msg.id === replyId ? { ...msg, ...fields } : msg)));

      try {
        await postChatStream(
          {
            conversationId: conversationId.current,
            company,
            division,
            mode,
            message: content,
          },
          {
            // Sources arrive before the first token — citations and the
            // knowledge-map highlight appear while the answer is still being written.
            onMeta: (meta) => patch({ category: meta.category, sources: meta.sources }),
            onDelta: (text) =>
              setMessages((m) =>
                m.map((msg) => (msg.id === replyId ? { ...msg, content: msg.content + text } : msg))
              ),
          }
        );
      } catch (err: any) {
        patch({ content: `Er ging iets mis: ${err.message}`, error: true });
      } finally {
        setLoading(false);
      }
    },
    [company, division, mode, loading]
  );

  const upload = useCallback(
    async (file: File) => {
      setLoading(true);
      try {
        const res = await postUpload(file, company, division);
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: 'assistant',
            content: `📎 **${res.filename}** toegevoegd aan de kennisbank (${res.chunks} fragmenten). Stel je vraag erover.`,
          },
        ]);
      } catch (err: any) {
        setMessages((m) => [
          ...m,
          { id: uid(), role: 'assistant', content: `Upload mislukt: ${err.message}`, error: true },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [company, division]
  );

  return {
    companies, company, setCompany,
    division, setDivision,
    mode, setMode,
    messages, input, setInput,
    loading, send, upload, newChat,
  };
}
